import { useEffect, useMemo, useRef, useState } from 'react'

import { useMutation, useQuery } from '@apollo/client'
import { Divider, Drawer, Text, TextInput, Tooltip } from '@mantine/core'

import AdminContainer from 'src/components/AdminContainer/AdminContainer'
import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useCustomSteps } from 'src/context/CustomStepsContext'
import {
  CREATE_CUSTOM_QUESTION,
  UPDATE_CUSTOM_QUESTION,
  ADD_QUESTION_TO_TEMPLATE,
  UPDATE_TEMPLATE_QUESTION,
  BATCH_UPDATE_TEMPLATE_QUESTIONS,
  REMOVE_QUESTION_FROM_TEMPLATE,
} from 'src/graphql/customQuestions.graphql'
import {
  CREATE_CUSTOM_STEP,
  GET_CUSTOM_STEPS,
} from 'src/graphql/customSteps.graphql'
import {
  CREATE_JOB_LISTING_TEMPLATE,
  UPDATE_JOB_LISTING_TEMPLATE,
  GET_JOB_LISTING_TEMPLATE,
} from 'src/graphql/jobListingTemplates.graphql'
import {
  CustomQuestionFieldType,
  CustomQuestionVisibility,
} from 'src/graphql/types/customQuestions'

import ChecklistSection, {
  AVAILABLE_STEP_TYPES,
  StepConfig,
} from './ChecklistSection'
import QuestionsSection, { QuestionConfig } from './QuestionsSection'

// Constants for validation
const QUESTION_LABEL_MAX_LENGTH = 1000
const QUESTION_LABEL_MIN_LENGTH = 2

const buildDefaultSteps = (
  customSteps: { customStepId: string; label: string }[]
): StepConfig[] => [
  ...AVAILABLE_STEP_TYPES.map((step, index) => ({
    stepType: step.value,
    enabled: true,
    order: index,
  })),
  ...customSteps.map((custom, index) => ({
    stepType: 'CUSTOM',
    customStepId: custom.customStepId,
    label: custom.label,
    enabled: false,
    order: AVAILABLE_STEP_TYPES.length + index,
  })),
]

interface JobListingTemplateDetailsDrawerProps {
  opened: boolean
  close: () => void
  templateId?: string
  refetch: () => void
}

const JobListingTemplateDetailsDrawer = ({
  opened,
  close,
  templateId,
  refetch,
}: JobListingTemplateDetailsDrawerProps) => {
  const customStepsCtx = useCustomSteps()
  const customSteps = useMemo(
    () => customStepsCtx?.customSteps ?? [],
    [customStepsCtx?.customSteps]
  )
  const customStepsRef = useRef(customSteps)
  customStepsRef.current = customSteps
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [steps, setSteps] = useState<StepConfig[]>(
    buildDefaultSteps(customStepsRef.current)
  )
  const [questions, setQuestions] = useState<QuestionConfig[]>([])
  const [activeSection, setActiveSection] = useState<string | null>('checklist')

  const isEditing = !!templateId

  const { data: templateData, loading: templateLoading } = useQuery(
    GET_JOB_LISTING_TEMPLATE,
    {
      variables: { id: templateId },
      skip: !templateId,
      fetchPolicy: 'network-only',
      onError: (error) => {
        toast('Error loading template', 'error')
      },
    }
  )

  const template = templateData?.jobListingTemplate

  useEffect(() => {
    if (!opened) {
      // Reset form when drawer closes
      setName('')
      setDescription('')
      setSteps(buildDefaultSteps(customStepsRef.current))
      setQuestions([])
      return
    }

    if (!templateId || !template) {
      // Create mode - reset to defaults
      setName('')
      setDescription('')
      setSteps(buildDefaultSteps(customStepsRef.current))
      setQuestions([])
      return
    }

    // Edit mode - populate form with template data
    setName(template.name || '')
    setDescription(template.description || '')

    const keyOf = (s: { stepType: string; customStepId?: string }) =>
      s.customStepId ?? s.stepType
    const existingKeys = new Set(template.steps.map(keyOf))
    const orderByKey = template.steps.reduce((acc, step) => {
      acc[keyOf(step)] = step.order
      return acc
    }, {})

    const templateCustomSteps = template.steps
      .filter((s) => s.customStepId)
      .map((s) => ({
        customStepId: s.customStepId,
        label: s.customStep?.label ?? '',
      }))
    const customStepDefs = [
      ...customStepsRef.current,
      ...templateCustomSteps,
    ].filter(
      (step, index, all) =>
        all.findIndex((s) => s.customStepId === step.customStepId) === index
    )

    const orderedSteps = buildDefaultSteps(customStepDefs)
      .map((stepDef) => ({
        ...stepDef,
        enabled: existingKeys.has(keyOf(stepDef)),
        order: orderByKey[keyOf(stepDef)] ?? 999,
      }))
      .sort((a, b) => {
        if (a.enabled && !b.enabled) return -1
        if (!a.enabled && b.enabled) return 1
        return a.order - b.order
      })

    setSteps(orderedSteps)

    // Map existing questions
    const existingQuestions = [...(template.questions || [])]
      .sort((a, b) => a.order - b.order)
      .map((tq) => {
        const sortedOptions = [...(tq.question.options || [])]
          .sort((a, b) => a.order - b.order)
          .map((opt) => ({
            id: opt.customQuestionOptionId,
            label: opt.label,
            order: opt.order,
          }))

        return {
          id: tq.jobListingTemplateQuestionId,
          customQuestionId: tq.customQuestionId,
          label: tq.question.label,
          key: tq.question.key,
          fieldType: tq.question.fieldType,
          isRequired: tq.isRequired,
          visibility:
            tq.visibility === CustomQuestionVisibility.PUBLIC
              ? CustomQuestionVisibility.PUBLIC
              : CustomQuestionVisibility.PRIVATE,
          options: sortedOptions,
          originalOptions: sortedOptions, // Store original options for comparison
          order: tq.order,
          isOpen: false,
        }
      })

    setQuestions(existingQuestions)
  }, [opened, templateId, template])

  useEffect(() => {
    if (!opened) return
    setSteps((prev) => {
      const presentKeys = new Set(prev.map((s) => s.customStepId ?? s.stepType))
      const missing = customSteps
        .filter((c) => !presentKeys.has(c.customStepId))
        .map((c, index) => ({
          stepType: 'CUSTOM',
          customStepId: c.customStepId,
          label: c.label,
          enabled: false,
          order: prev.length + index,
        }))
      return missing.length ? [...prev, ...missing] : prev
    })
  }, [customSteps, opened])

  const [createTemplate, { loading: createLoading }] = useMutation(
    CREATE_JOB_LISTING_TEMPLATE,
    {
      onError: (error) => {
        toast(error.message || 'Unable to create template', 'error')
      },
    }
  )

  const [updateTemplate, { loading: updateLoading }] = useMutation(
    UPDATE_JOB_LISTING_TEMPLATE,
    {
      onError: (error) => {
        toast(error.message || 'Unable to update template', 'error')
      },
    }
  )

  const [createCustomQuestion] = useMutation(CREATE_CUSTOM_QUESTION, {
    onError: (error) => {
      toast(error.message || 'Unable to create question', 'error')
    },
  })

  const [createCustomStep, { loading: creatingCustomStep }] = useMutation(
    CREATE_CUSTOM_STEP,
    {
      refetchQueries: [GET_CUSTOM_STEPS],
      onError: (error) => {
        toast(error.message || 'Unable to create custom step', 'error')
      },
    }
  )

  const handleCreateCustomStep = async (label: string): Promise<boolean> => {
    try {
      const { data } = await createCustomStep({
        variables: { input: { label } },
      })
      const created = data?.createCustomStep
      if (!created) return false

      setSteps((prev) => {
        const alreadyPresent = prev.some(
          (step) => step.customStepId === created.customStepId
        )
        if (alreadyPresent) {
          return prev.map((step) =>
            step.customStepId === created.customStepId
              ? { ...step, enabled: true }
              : step
          )
        }
        return [
          ...prev,
          {
            stepType: 'CUSTOM',
            customStepId: created.customStepId,
            label: created.label,
            enabled: true,
            order: prev.length,
          },
        ]
      })
      return true
    } catch {
      // onError already surfaces the failure
      return false
    }
  }

  const [updateCustomQuestion] = useMutation(UPDATE_CUSTOM_QUESTION, {
    onError: (error) => {
      toast(error.message || 'Unable to update question', 'error')
    },
  })

  const [addQuestionToTemplate] = useMutation(ADD_QUESTION_TO_TEMPLATE, {
    onError: (error) => {
      toast(error.message || 'Unable to add question to template', 'error')
    },
  })

  const [updateTemplateQuestion] = useMutation(UPDATE_TEMPLATE_QUESTION, {
    onError: (error) => {
      toast(error.message || 'Unable to update template question', 'error')
    },
  })

  const [batchUpdateTemplateQuestions] = useMutation(
    BATCH_UPDATE_TEMPLATE_QUESTIONS,
    {
      onError: (error) => {
        toast(error.message || 'Unable to update template questions', 'error')
      },
    }
  )

  const [removeQuestionFromTemplate] = useMutation(
    REMOVE_QUESTION_FROM_TEMPLATE,
    {
      onError: (error) => {
        toast(
          error.message || 'Unable to remove question from template',
          'error'
        )
      },
    }
  )

  const handleSave = async () => {
    if (!name.trim()) {
      toast('Template name is required', 'error')
      return
    }

    const enabledSteps = steps
      .filter((step) => step.enabled)
      .map((step, index) => ({
        stepType: step.stepType,
        customStepId: step.customStepId,
        order: index,
      }))

    if (enabledSteps.length === 0) {
      toast('At least one step must be enabled', 'error')
      return
    }

    // Validate questions
    for (const question of questions) {
      if (
        !question.label ||
        question.label.trim().length < QUESTION_LABEL_MIN_LENGTH
      ) {
        toast(
          `All questions must have a label of at least ${QUESTION_LABEL_MIN_LENGTH} characters`,
          'error'
        )
        return
      }
      if (question.label.length > QUESTION_LABEL_MAX_LENGTH) {
        toast(
          `Question labels cannot exceed ${QUESTION_LABEL_MAX_LENGTH} characters`,
          'error'
        )
        return
      }
      if (!question.fieldType) {
        toast('All questions must have a type selected', 'error')
        return
      }
      if (typeof question.isRequired !== 'boolean') {
        toast('All questions must have a required status selected', 'error')
        return
      }
      if (
        question.fieldType === CustomQuestionFieldType.SINGLE_SELECT ||
        question.fieldType === CustomQuestionFieldType.MULTI_SELECT
      ) {
        if (!question.options || question.options.length < 2) {
          toast(
            'Single/Multi select questions must have at least 2 options',
            'error'
          )
          return
        }
        if (
          question.options.some(
            (opt) => !opt.label || opt.label.trim().length === 0
          )
        ) {
          toast('All options must have a label', 'error')
          return
        }
      }
    }

    const input = {
      name: name.trim(),
      description: description.trim() || null,
      steps: enabledSteps,
    }

    try {
      let savedTemplateId = templateId

      // Create or update the template
      if (isEditing) {
        await updateTemplate({
          variables: {
            id: templateId,
            input,
          },
        })
      } else {
        const createResult = await createTemplate({
          variables: {
            input,
          },
        })
        savedTemplateId =
          createResult.data?.createJobListingTemplate?.jobListingTemplateId
      }

      // Process questions if template was saved successfully
      if (savedTemplateId) {
        try {
          // If editing, determine which questions to add, update, or remove
          if (isEditing && template?.questions) {
            const existingTemplateQuestions = new Map(
              template.questions.map((tq) => [
                tq.jobListingTemplateQuestionId,
                tq,
              ])
            )
            const currentQuestionIds = new Set(
              questions.filter((q) => q.id).map((q) => q.id)
            )

            // Remove questions that are no longer in the list
            for (const tq of template.questions) {
              if (!currentQuestionIds.has(tq.jobListingTemplateQuestionId)) {
                await removeQuestionFromTemplate({
                  variables: { id: tq.jobListingTemplateQuestionId },
                })
              }
            }

            // Separate questions into: new to create/add, existing to update
            const questionsToUpdate = []
            const questionsToAdd = []

            for (let i = 0; i < questions.length; i++) {
              const question = questions[i]
              let questionId = question.customQuestionId

              // If this is a new question (no customQuestionId), create it first
              if (!questionId) {
                const questionInput = {
                  label: question.label.trim(),
                  key: question.key?.trim() || null,
                  fieldType: question.fieldType,
                  ...(question.options && question.options.length > 0
                    ? {
                        options: question.options.map((opt, idx) => ({
                          label: opt.label.trim(),
                          order: idx,
                        })),
                      }
                    : {}),
                }

                const createQuestionResult = await createCustomQuestion({
                  variables: { input: questionInput },
                })
                questionId =
                  createQuestionResult.data?.createCustomQuestion
                    ?.customQuestionId

                // Queue to add to template
                if (questionId) {
                  questionsToAdd.push({
                    customQuestionId: questionId,
                    isRequired: question.isRequired,
                    visibility:
                      question.visibility ?? CustomQuestionVisibility.PRIVATE,
                    order: i,
                  })
                }
              } else {
                // Update the custom question itself if it has changes
                const currentOptionIds = new Set(
                  (question.options || []).map((opt) => opt.id)
                )

                // Build options array with existing and new options
                const updatedOptions = (question.options || []).map(
                  (opt, idx) => ({
                    ...(opt.id.startsWith('option-')
                      ? {}
                      : { customQuestionOptionId: opt.id }),
                    label: opt.label.trim(),
                    order: idx,
                  })
                )

                // Find removed options (in original but not in current)
                const removedOptions = (question.originalOptions || [])
                  .filter((opt) => !currentOptionIds.has(opt.id))
                  .map((opt, idx) => ({
                    customQuestionOptionId: opt.id,
                    label: opt.label,
                    order: updatedOptions.length + idx,
                    archived: true,
                  }))

                // Combine updated and removed options
                const allOptions = [...updatedOptions, ...removedOptions]

                const questionInput = {
                  label: question.label.trim(),
                  key: question.key?.trim() || null,
                  ...(allOptions.length > 0 ? { options: allOptions } : {}),
                }

                await updateCustomQuestion({
                  variables: {
                    id: questionId,
                    input: questionInput,
                  },
                })

                // Check if template question exists
                if (question.id && existingTemplateQuestions.has(question.id)) {
                  // Queue for batch update
                  questionsToUpdate.push({
                    id: question.id,
                    isRequired: question.isRequired,
                    visibility:
                      question.visibility ?? CustomQuestionVisibility.PRIVATE,
                    order: i,
                  })
                } else {
                  // Queue to add to template
                  questionsToAdd.push({
                    customQuestionId: questionId,
                    isRequired: question.isRequired,
                    visibility:
                      question.visibility ?? CustomQuestionVisibility.PRIVATE,
                    order: i,
                  })
                }
              }
            }

            // Batch update existing template questions
            if (questionsToUpdate.length > 0) {
              await batchUpdateTemplateQuestions({
                variables: {
                  jobListingTemplateId: savedTemplateId,
                  updates: questionsToUpdate,
                },
              })
            }

            // Add new template questions
            for (const questionToAdd of questionsToAdd) {
              await addQuestionToTemplate({
                variables: {
                  jobListingTemplateId: savedTemplateId,
                  input: {
                    customQuestionId: questionToAdd.customQuestionId,
                    isRequired: questionToAdd.isRequired,
                    visibility:
                      questionToAdd.visibility ??
                      CustomQuestionVisibility.PRIVATE,
                    order: questionToAdd.order,
                  },
                },
              })
            }
          } else {
            // Creating new template - add all questions
            for (let i = 0; i < questions.length; i++) {
              const question = questions[i]
              let questionId = question.customQuestionId

              // If this is a new question (no customQuestionId), create it first
              if (!questionId) {
                const questionInput = {
                  label: question.label.trim(),
                  key: question.key?.trim() || null,
                  fieldType: question.fieldType,
                  ...(question.options && question.options.length > 0
                    ? {
                        options: question.options.map((opt, idx) => ({
                          label: opt.label.trim(),
                          order: idx,
                        })),
                      }
                    : {}),
                }

                const createQuestionResult = await createCustomQuestion({
                  variables: { input: questionInput },
                })
                questionId =
                  createQuestionResult.data?.createCustomQuestion
                    ?.customQuestionId
              }

              // Add question to template
              if (questionId) {
                await addQuestionToTemplate({
                  variables: {
                    jobListingTemplateId: savedTemplateId,
                    input: {
                      customQuestionId: questionId,
                      isRequired: question.isRequired,
                      visibility:
                        question.visibility ?? CustomQuestionVisibility.PRIVATE,
                      order: i,
                    },
                  },
                })
              }
            }
          }
        } catch (questionError) {
          // If any question operation fails, show error and suggest refreshing
          console.error('Error processing questions:', questionError)
          toast(
            'Template saved but there was an error processing questions. Please refresh and try again.',
            'error'
          )
          // Don't throw - template was saved successfully
          refetch()
          close()
          return
        }
      }

      toast(
        isEditing
          ? 'Template updated successfully'
          : 'Template created successfully',
        'success'
      )
      refetch()
      close()
    } catch (error) {
      // Error toasts are handled by mutation onError callbacks
      console.error('Error saving template:', error)
    }
  }

  const loading = createLoading || updateLoading

  // Check if save should be disabled
  const isSaveDisabled = () => {
    // Check name
    if (!name.trim()) return true

    // Check at least one step is enabled
    if (!steps.some((step) => step.enabled)) return true

    // Check questions validation
    for (const question of questions) {
      // Question label validation
      if (
        !question.label ||
        question.label.trim().length < QUESTION_LABEL_MIN_LENGTH ||
        question.label.length > QUESTION_LABEL_MAX_LENGTH
      ) {
        return true
      }
      // Field type validation
      if (!question.fieldType) return true
      // Required validation
      if (typeof question.isRequired !== 'boolean') {
        return true
      }
      // Options validation for select types
      if (
        question.fieldType === CustomQuestionFieldType.SINGLE_SELECT ||
        question.fieldType === CustomQuestionFieldType.MULTI_SELECT
      ) {
        if (!question.options || question.options.length < 2) {
          return true
        }
        if (
          question.options.some(
            (opt) => !opt.label || opt.label.trim().length === 0
          )
        ) {
          return true
        }
      }
    }

    return false
  }

  return (
    <Drawer
      opened={opened}
      onClose={close}
      position="right"
      size="30%"
      offset={6}
      radius="md"
      withCloseButton={false}
      overlayProps={{ backgroundOpacity: 0.2 }}
      classNames={{
        title: 'text-base font-semibold text-doubleNickel-gray-900',
        body: 'flex flex-col gap-4 h-full',
      }}
    >
      <div className="flex h-full flex-col gap-4">
        <div className="flex flex-row items-center justify-between text-sm font-medium">
          <div className="flex-1 text-base font-semibold text-doubleNickel-gray-900">
            {isEditing
              ? 'Edit Job Listing Template'
              : 'Create Job Listing Template'}
          </div>
          <Tooltip
            label="Please fill in all required fields"
            withArrow
            disabled={!isSaveDisabled()}
          >
            <DNButton
              variant="primary"
              onClick={handleSave}
              loading={loading}
              disabled={isSaveDisabled()}
              className="w-[75px]"
            >
              {isEditing ? 'Save' : 'Create'}
            </DNButton>
          </Tooltip>
        </div>
        <Divider variant="dashed" />

        <div className="flex h-full flex-1 flex-col gap-3 overflow-y-auto">
          <div className="space-y-4">
            <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
              OVERVIEW
            </Text>
            <div className="space-y-3">
              <div className="flex flex-row items-center justify-between">
                <Text size="xs">
                  Template Name
                  <span className="text-doubleNickel-error-500">{' *'}</span>
                </Text>
                <div className="w-[250px]">
                  <TextInput
                    placeholder="e.g., Standard DOT Driver Onboarding"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                    size="xs"
                    classNames={{
                      input: 'text-xs text-right',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <Divider variant="dashed" />

          <ChecklistSection
            steps={steps}
            onStepsChange={setSteps}
            activeSection={activeSection}
            onActiveSectionChange={setActiveSection}
            onCreateCustomStep={handleCreateCustomStep}
            creatingCustomStep={creatingCustomStep}
          />

          <Divider variant="dashed" />

          <QuestionsSection
            opened={opened}
            questions={questions}
            onQuestionsChange={setQuestions}
            activeSection={activeSection}
            onActiveSectionChange={setActiveSection}
          />
        </div>
      </div>
    </Drawer>
  )
}

export default JobListingTemplateDetailsDrawer
